export interface GrupoEmpresa {
  id: string;
  nome: string;
  descricao: string;
  criadoPor: string; // empresaId da empresa criadora
  membros: string[]; // empresaIds das empresas membros (inclui criador)
  dataCriacao: string;
  ativo: boolean;
}

export interface ConviteGrupo {
  id: string;
  grupoId: string;
  grupoNome: string;
  deEmpresaId: string;
  deEmpresaNome: string;
  paraEmpresaId: string;
  status: 'pendente' | 'aprovado' | 'rejeitado';
  dataEnvio: string;
  dataResposta?: string;
}

// Grupos existentes
export const gruposEmpresas: GrupoEmpresa[] = [
  {
    id: 'GRUPO-001',
    nome: 'Grupo Financeiro',
    descricao: 'Grupo das empresas do segmento financeiro e incentivos',
    criadoPor: 'EMP-001',
    membros: ['EMP-001', 'EMP-003'],
    dataCriacao: '2026-03-15T10:00:00Z',
    ativo: true,
  },
  {
    id: 'GRUPO-002',
    nome: 'Grupo Tecnologia',
    descricao: 'Grupo das empresas de tecnologia e software',
    criadoPor: 'EMP-002',
    membros: ['EMP-002', 'EMP-004'],
    dataCriacao: '2026-04-20T14:30:00Z',
    ativo: true,
  },
];

// Convites pendentes
export const convitesGrupo: ConviteGrupo[] = [
  {
    id: 'CONV-001',
    grupoId: 'GRUPO-001',
    grupoNome: 'Grupo Financeiro',
    deEmpresaId: 'EMP-001',
    deEmpresaNome: 'getPoints',
    paraEmpresaId: 'EMP-003',
    status: 'aprovado',
    dataEnvio: '2026-03-10T09:00:00Z',
    dataResposta: '2026-03-12T11:00:00Z',
  },
  {
    id: 'CONV-002',
    grupoId: 'GRUPO-002',
    grupoNome: 'Grupo Tecnologia',
    deEmpresaId: 'EMP-002',
    deEmpresaNome: 'Inovação Digital S.A.',
    paraEmpresaId: 'EMP-004',
    status: 'aprovado',
    dataEnvio: '2026-04-18T15:00:00Z',
    dataResposta: '2026-04-19T10:00:00Z',
  },
];

export function getGrupoByEmpresaId(empresaId: string): GrupoEmpresa | undefined {
  return gruposEmpresas.find(g => g.membros.includes(empresaId));
}

export function getGrupoById(grupoId: string): GrupoEmpresa | undefined {
  return gruposEmpresas.find(g => g.id === grupoId);
}

export function getEmpresasDoGrupo(grupoId: string): string[] {
  const grupo = getGrupoById(grupoId);
  return grupo?.membros || [];
}

export function getEmpresasDoMesmoGrupo(empresaId: string): string[] {
  const grupo = getGrupoByEmpresaId(empresaId);
  return grupo?.membros || [];
}

export function isEmpresaNoGrupo(empresaId: string): boolean {
  return gruposEmpresas.some(g => g.membros.includes(empresaId));
}

export function getConvitesPendentes(empresaId: string): ConviteGrupo[] {
  return convitesGrupo.filter(c => c.paraEmpresaId === empresaId && c.status === 'pendente');
}

export function getConvitesEnviados(empresaId: string): ConviteGrupo[] {
  return convitesGrupo.filter(c => c.deEmpresaId === empresaId);
}

export function criarGrupo(nome: string, descricao: string, criadoPor: string): GrupoEmpresa {
  const novoGrupo: GrupoEmpresa = {
    id: `GRUPO-${String(gruposEmpresas.length + 1).padStart(3, '0')}`,
    nome,
    descricao,
    criadoPor,
    membros: [criadoPor],
    dataCriacao: new Date().toISOString(),
    ativo: true,
  };
  gruposEmpresas.push(novoGrupo);
  return novoGrupo;
}

export function enviarConvite(grupoId: string, deEmpresaId: string, deEmpresaNome: string, paraEmpresaId: string): ConviteGrupo {
  const grupo = getGrupoById(grupoId);
  const novoConvite: ConviteGrupo = {
    id: `CONV-${String(convitesGrupo.length + 1).padStart(3, '0')}`,
    grupoId,
    grupoNome: grupo?.nome || '',
    deEmpresaId,
    deEmpresaNome,
    paraEmpresaId,
    status: 'pendente',
    dataEnvio: new Date().toISOString(),
  };
  convitesGrupo.push(novoConvite);
  return novoConvite;
}

export function responderConvite(conviteId: string, aprovado: boolean): ConviteGrupo | null {
  const convite = convitesGrupo.find(c => c.id === conviteId);
  if (!convite || convite.status !== 'pendente') return null;

  convite.status = aprovado ? 'aprovado' : 'rejeitado';
  convite.dataResposta = new Date().toISOString();

  if (aprovado) {
    const grupo = getGrupoById(convite.grupoId);
    if (grupo && !grupo.membros.includes(convite.paraEmpresaId)) {
      grupo.membros.push(convite.paraEmpresaId);
    }
  }

  return convite;
}

export function removerEmpresaDoGrupo(grupoId: string, empresaId: string): boolean {
  const grupo = getGrupoById(grupoId);
  if (!grupo) return false;

  // Não permite remover o criador
  if (grupo.criadoPor === empresaId) return false;

  grupo.membros = grupo.membros.filter(id => id !== empresaId);
  return true;
}

export function dissolverGrupo(grupoId: string): boolean {
  const index = gruposEmpresas.findIndex(g => g.id === grupoId);
  if (index === -1) return false;

  gruposEmpresas[index].ativo = false;
  gruposEmpresas[index].membros = [];
  return true;
}

export function editarGrupo(grupoId: string, nome: string, descricao: string): GrupoEmpresa | null {
  const grupo = getGrupoById(grupoId);
  if (!grupo) return null;
  grupo.nome = nome;
  grupo.descricao = descricao;
  return grupo;
}

export function cancelarConvite(conviteId: string): boolean {
  const convite = convitesGrupo.find(c => c.id === conviteId);
  if (!convite || convite.status !== 'pendente') return false;
  convite.status = 'rejeitado';
  convite.dataResposta = new Date().toISOString();
  return true;
}